import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AnalysisResult } from "@/lib/api/schema-analyzer";

/**
 * Hook to restore the user's most recent analysis results from history.
 * Returns the last session's results so they persist across page reloads.
 */
export function useRestoredResults() {
  const { user } = useAuth();
  const [restoredResults, setRestoredResults] = useState<AnalysisResult[]>([]);
  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    if (!user) {
      setRestoredResults([]);
      setIsRestored(false);
      return;
    }

    const restore = async () => {
      try {
        // Load the latest analyses from the last 2 hours (= likely same session)
        const twoHoursAgo = new Date();
        twoHoursAgo.setHours(twoHoursAgo.getHours() - 2);

        const { data, error } = await supabase
          .from("analysis_history")
          .select("url, page_type, title, schemas_data")
          .eq("user_id", user.id)
          .gte("created_at", twoHoursAgo.toISOString())
          .order("created_at", { ascending: false })
          .limit(10);

        if (error || !data || data.length === 0) {
          setIsRestored(true);
          return;
        }

        const results: AnalysisResult[] = data.map((item) => ({
          url: item.url,
          pageType: item.page_type || "Unknown",
          title: item.title || item.url,
          existingSchemas: [],
          suggestedSchemas: [],
          generatedSchemas: Array.isArray(item.schemas_data)
            ? (item.schemas_data as any[])
            : [],
          warnings: [],
        }));

        setRestoredResults(results);
        setIsRestored(true);
      } catch (e) {
        console.error("Failed to restore results:", e);
        setIsRestored(true);
      }
    };

    restore();
  }, [user]);

  return { restoredResults, isRestored };
}
